import * as React from 'react';
import { Box, Container, Typography, Grid, styled, useTheme } from '@mui/material';
import BrainIcon from '@mui/icons-material/Psychology';
import SearchIcon from '@mui/icons-material/Search';
import TargetIcon from '@mui/icons-material/TrackChanges';
import { DescriptionOutlined, LightbulbOutlined, TaskOutlined } from '@mui/icons-material';
const FeatureSectionBackground = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    right: 0,
    width: 384,
    height: 384,
    backgroundColor: '#DBEAFE',
    borderRadius: '50%',
    filter: 'blur(48px)',
    opacity: 0.2,
    transform: 'translate(50%, -50%)',
}));

const FeatureSectionBackgroundBottom = styled(Box)(({ theme }) => ({
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: 384,
    height: 384,
    backgroundColor: '#EEF2FF',
    borderRadius: '50%',
    filter: 'blur(48px)',
    opacity: 0.2,
    transform: 'translate(-50%, 50%)',
}));

const featuresData = [
    { icon: BrainIcon, title: 'AI-Powered Root Cause Analysis', description: 'Advanced AI analyzes 247+ data points to identify root causes in under 5 minutes, replacing weeks of manual investigation.', gradient: 'linear-gradient(to bottom right, #1E5BB8, #0047AB)', stat: '5 min' },
    { icon: DescriptionOutlined, title: 'Smart Problem Definition', description: 'AI-assisted problem statements ensure clear, actionable documentation from the start with intelligent suggestions.', gradient: 'linear-gradient(to bottom right, #3B82F6, #4F46E5)', stat: '99% accurate' },
    { icon: SearchIcon, title: 'Advanced NC Search', description: 'Find and analyze related non-conformities instantly with our powerful search technology and pattern recognition.', gradient: 'linear-gradient(to bottom right, #6366F1, #9333EA)', stat: 'Instant' },
    { icon: LightbulbOutlined, title: 'Corrective Action Recommendations', description: 'Get AI-driven suggestions for effective corrective actions based on industry best practices and historical data.', gradient: 'linear-gradient(to bottom right, #8B5CF6, #7C3AED)', stat: '97% success' },
    { icon: TaskOutlined, title: 'One-Click Report Generation', description: 'Generate comprehensive 8D reports instantly for customers, auditors, and management with a single click.', gradient: 'linear-gradient(to bottom right, #A855F7, #EC4899)', stat: '1-Click' },
    { icon: TargetIcon, title: '3RC Root Cause Method', description: 'Triple root cause analysis with NDRC, PRC, and SRC methodologies for thorough problem investigation.', gradient: 'linear-gradient(to bottom right, #F472B6, #EC4899)', stat: '3x depth' }
];

export default function Features({ siteSettings = {} }) {
    const theme = useTheme();

    return (
        <Box
            component="section"
            id="features"
            sx={{
                py: { xs: 8, md: 12 },
                backgroundColor: 'white',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <FeatureSectionBackground />
            <FeatureSectionBackgroundBottom />

            <Container maxWidth="xl" sx={{ px: 3, position: 'relative', zIndex: 10 }}>

                <Box sx={{ textAlign: 'center', maxWidth: 680, mx: 'auto', mb: { xs: 8, md: 10 } }}>
                    <Box sx={{ display: 'inline-block', mb: 2 }}>
                        <Typography
                            component="span"
                            sx={{
                                background: 'linear-gradient(to right, #1E5BB8, #0047AB)',
                                color: 'white',
                                px: 3,
                                py: 1,
                                borderRadius: 999,
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                textTransform: 'uppercase',
                                letterSpacing: 1.5,
                                boxShadow: theme.shadows[3],
                            }}
                        >
                            Powerful Features
                        </Typography>
                    </Box>
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: '2.5rem', md: '3rem' },
                            fontWeight: 700,
                            color: 'gray.900',
                            mb: 3,
                            lineHeight: 1.1,
                        }}
                    >
                        Everything You Need for{' '}
                        <Box component="span" sx={{
                            background: 'linear-gradient(to right, #1E5BB8, #0047AB)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            textFillColor: 'transparent',
                        }}>
                            Quality Excellence
                        </Box>
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '1.25rem',
                            color: 'gray.600',
                        }}
                    >
                        Cutting-edge AI technology combined with proven quality management methodologies
                    </Typography>
                </Box>

                <Grid container spacing={4}>
                    {featuresData.map((feature, index) => {
                        const IconComponent = feature.icon;
                        return (
                            <Grid item xs={12} md={6} lg={4} key={index}>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        backgroundColor: 'white',
                                        borderRadius: '16px',
                                        p: 4,
                                        border: '2px solid #F3F4F6',
                                        transition: 'all 0.3s',
                                        overflow: 'hidden',
                                        '&:hover': {
                                            borderColor: '#1E5BB8',
                                            boxShadow: theme.shadows[15],
                                        },
                                        '&:hover > div:first-of-type': {
                                            opacity: 1,
                                        },
                                        '&:hover .gradient-divider': {
                                            width: '100%',
                                        },
                                        '&:hover .icon-box': {
                                            transform: 'scale(1.1)',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            width: 128,
                                            height: 128,
                                            background: 'linear-gradient(to bottom right, #EFF6FF, #EEF2FF)',
                                            borderRadius: '50%',
                                            filter: 'blur(32px)',
                                            opacity: 0,
                                            transition: 'opacity 0.3s',
                                            transform: 'translate(50%, -50%)',
                                            zIndex: 0,
                                        }}
                                    />

                                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
                                            <Box
                                                className="icon-box"
                                                sx={{
                                                    width: 64,
                                                    height: 64,
                                                    borderRadius: '12px',
                                                    background: feature.gradient,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: theme.shadows[3],
                                                    transition: 'transform 0.3s',
                                                }}
                                            >
                                                <IconComponent sx={{ width: 32, height: 32, color: 'white' }} />
                                            </Box>
                                            <Box
                                                sx={{
                                                    px: 2,
                                                    py: 1,
                                                    borderRadius: 999,
                                                    background: feature.gradient,
                                                    color: 'white',
                                                    fontWeight: 700,
                                                    fontSize: '0.875rem',
                                                    boxShadow: theme.shadows[3],
                                                }}
                                            >
                                                {feature.stat}
                                            </Box>
                                        </Box>

                                        <Typography
                                            variant="h3"
                                            sx={{
                                                fontSize: '1.25rem',
                                                fontWeight: 700,
                                                color: 'gray.900',
                                                mb: 2,
                                                lineHeight: 1.25,
                                            }}
                                        >
                                            {feature.title}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: 'gray.600',
                                                lineHeight: 1.625,
                                            }}
                                        >
                                            {feature.description}
                                        </Typography>

                                        <Box
                                            className="gradient-divider"
                                            sx={{
                                                mt: 3,
                                                height: 4,
                                                width: 0,
                                                background: feature.gradient,
                                                borderRadius: 999,
                                                transition: 'width 0.5s',
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>

                <Box sx={{ mt: 8, textAlign: 'center' }}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            background: 'linear-gradient(to right, #1E5BB8, #0047AB)',
                            color: 'white',
                            px: { xs: 6, md: 6 },
                            py: { xs: 4, md: 4 },
                            borderRadius: '16px',
                            boxShadow: theme.shadows[15],
                        }}
                    >
                        <Typography sx={{ fontSize: '3rem', fontWeight: 700 }}>{siteSettings.quality_issues_resolved || '12.5k+'}</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '1.125rem' }}>Quality Issues Resolved</Typography>
                        <Typography variant="body2" sx={{ color: '#93C5FD' }}>{siteSettings.trusted_by_text || 'Trusted by 30+ Manufacturing Companies Worldwide'}</Typography>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}
